import { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

/**
 * BeforeAfterSlider — draggable comparison of two images.
 * Props:
 *  - beforeSrc, afterSrc: image URLs
 *  - beforeLabel, afterLabel: chip captions
 */
export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel = "Vorher",
  afterLabel = "Nachher",
  aspectClass = "aspect-[3/4]",
}) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const wrapRef = useRef(null);

  const updateFromEvent = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onMouseDown = (e) => {
    setDragging(true);
    updateFromEvent(e.clientX);
  };
  const onTouchStart = (e) => {
    setDragging(true);
    if (e.touches[0]) updateFromEvent(e.touches[0].clientX);
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      if (e.touches && e.touches[0]) updateFromEvent(e.touches[0].clientX);
      else if (e.clientX !== undefined) updateFromEvent(e.clientX);
    };
    const end = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [dragging, updateFromEvent]);

  return (
    <div
      ref={wrapRef}
      data-testid="ba-slider"
      onMouseDown={onMouseDown}
      className={`relative ${aspectClass} w-full overflow-hidden rounded-sm bg-yx-surface select-none md:cursor-ew-resize`}
    >
      {/* AFTER (bottom layer, full) */}
      <img
        src={afterSrc}
        alt={afterLabel}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* BEFORE (top, clipped to pos%) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeLabel}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ width: `${100 / (pos / 100 || 0.0001)}%`, maxWidth: "none" }}
        />
      </div>

      {/* Labels */}
      <span
        data-testid="ba-label-before"
        className="absolute top-4 left-4 z-10 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-yx-primary shadow-sm"
      >
        {beforeLabel}
      </span>
      <span
        data-testid="ba-label-after"
        className="absolute top-4 right-4 z-10 rounded-full bg-yx-primary text-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest shadow-sm"
      >
        {afterLabel}
      </span>

      {/* Divider + handle */}
      <div
        aria-hidden
        className="absolute top-0 bottom-0 w-[2px] bg-white/90 pointer-events-none"
        style={{ left: `calc(${pos}% - 1px)` }}
      />
      <motion.button
        type="button"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        data-testid="ba-handle"
        aria-label="Vergleichs-Slider"
        style={{ left: `calc(${pos}% - 22px)` }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="absolute top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white shadow-xl flex items-center justify-center ring-1 ring-black/5 cursor-grab active:cursor-grabbing touch-none"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5 text-yx-primary" fill="currentColor">
          <path d="M8 5l-6 7 6 7V5zm8 0v14l6-7-6-7z" />
        </svg>
      </motion.button>

      {/* Range slider for keyboard accessibility */}
      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Vergleich"
        className="sr-only"
      />
    </div>
  );
}
